import { Collections } from 'Collections';
import { FullscreenLoading } from 'components/FullscreenLoading';
import { auth } from "FirebaseConfigs";
import { FirebaseUtils } from 'firebaseUtils/FirebaseUtils';
import { AnalyticsEvent } from 'models/AnalyticsEvent';
import { AppUser } from 'models/AppUser';
import { avatarImageFrom } from 'models/AvatarImage';
import { emailFrom } from 'models/Email';
import { nameFrom } from 'models/Name';
import { phoneFrom } from 'models/Phone';
import { isEmpty } from 'Util/isEmpty';
import { AppBar, Button, List, ListItem, TextField, Toolbar, Typography } from "@material-ui/core";
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useMemo } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import * as yup from 'yup';

export function splitName(name?: string | null) {
    if (isEmpty(name)) {
        return null
    }
    const list = name?.split(' ')
    if (list.length <= 1) {
        return { firstName: name, lastName: null }
    }
    const firstName = list.slice(0, list.length - 1).join(' ')
    const lastName = list[list.length - 1]
    return { firstName, lastName }
}

export function CreateAppUserScreen() {
    const { enqueueSnackbar } = useSnackbar()

    const [authUser, loadingAuthUser] = useAuthState(auth)

    const authUserName = useMemo(() => splitName(authUser?.displayName), [authUser])
    const formik = useFormik({
        initialValues: {
            firstName: authUserName?.firstName || '',
            lastName: authUserName?.lastName || '',
            email: authUser?.email || '',
            phone: authUser?.phoneNumber || '',
        },
        validateOnMount: true,
        validationSchema: yup.object({
            phone: yup.string().matches(/\d{8}/, 'Telefonnummeret skal bestå av 8 siffer'),
        }),
        onSubmit({ firstName, lastName, email, phone }) {
            if (authUser) {
                FirebaseUtils.createDocument<AppUser>(
                    true,
                    Collections.APP_USERS,
                    authUser?.uid,
                    null,
                    null,
                    AnalyticsEvent.CREATED_APP_USER,
                    AnalyticsEvent.CREATED_APP_USER,
                    AnalyticsEvent.CREATE_APP_USER_ERROR,
                    enqueueSnackbar,
                    'App Bruker opprettet',
                    {
                        contactInfo: {
                            name: nameFrom(firstName, lastName),
                            phone: phoneFrom(phone),
                            email: emailFrom(email),
                        },
                        avatarImage: avatarImageFrom(auth.currentUser?.photoURL || null),
                        appRoles: {
                            map: {},
                        },
                    },
                    (doc) => ({
                        authUserId: authUser?.uid,
                        appUserId: doc.id,
                    })
                )
            }
        },
    })

    return (
        <div>
            <AppBar>
                <Toolbar>
                    <Typography variant="h6" color="inherit" noWrap>Hvem er du?</Typography>
                </Toolbar>
            </AppBar>
            <Toolbar />
            <FullscreenLoading screenId="CreateAppUser" loading={loadingAuthUser}>
                <form onSubmit={formik.handleSubmit}>
                    <List>
                        <ListItem>
                            <TextField
                                autoFocus
                                type="text"
                                variant="outlined"
                                fullWidth
                                label="Fornavn"
                                id="name"
                                aria-label="name"
                                name="firstName"
                                autoComplete="given-name additional-name"
                                value={formik.values.firstName}
                                onChange={formik.handleChange}
                                helperText={formik.errors.firstName}
                            />
                        </ListItem>
                        <ListItem>
                            <TextField
                                type="text"
                                variant="outlined"
                                fullWidth
                                label="Etternavn"
                                id="family-name"
                                aria-label="family-name"
                                autoComplete="family-name"
                                name="lastName"
                                value={formik.values.lastName}
                                onChange={formik.handleChange}
                                helperText={formik.errors.lastName}
                            />
                        </ListItem>
                        <ListItem>
                            <TextField
                                type="text"
                                variant="outlined"
                                fullWidth
                                label="Personlig E-post"
                                id="email"
                                aria-label="email"
                                autoComplete="email"
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                helperText={formik.errors.email}
                            />
                        </ListItem>
                        <ListItem>
                            <TextField
                                type="tel"
                                variant="outlined"
                                fullWidth
                                label="Tlf"
                                id="phone"
                                aria-label="phone"
                                autoComplete="tel-national"
                                name="phone"
                                error={formik.touched.phone && !!formik.errors.phone}
                                value={formik.values.phone}
                                onChange={formik.handleChange}
                                helperText={formik.touched.phone && formik.errors.phone}
                            />
                        </ListItem>
                        <ListItem>
                            <Button
                                fullWidth
                                color="primary"
                                variant="contained"
                                type="submit"
                            >Ok</Button>
                        </ListItem>
                    </List>
                </form>
            </FullscreenLoading>
        </div >
    )
}